import {
  SessionNotificationDetailCalendarActionEnum,
  SessionNotificationItemRecipientKindEnum,
  type GetSessionNotificationDetailDto,
  type SessionNotificationDetail,
  type SessionNotificationItem,
} from "@simbelapp/order-sdk";
import { BookingEventsEnum } from "~/utils/enums/bookings-events.enums";
import { SimbelEmail } from "~/utils/enums/common.enums";
import {
  ATTENDEE_LIST_ATTACHMENT_LABEL,
  AUTOMATIC_SENDER_LABEL,
  CALENDAR_INVITATION_TAG,
  SESSION_NOTIFICATION_EVENT_TAG,
  SESSION_NOTIFICATION_VISIBLE_RECIPIENTS,
  TRAINER_NOTIFICATION_CAUSE_LABEL,
} from "~/utils/enums/session-notifications.enums";
import type {
  ISessionNotificationAttachmentRow,
  ISessionNotificationEventTag,
} from "~/utils/interfaces/session-notifications-interfaces";

/** A list row and its detail share every field read here. */
type SessionNotificationEntry = SessionNotificationItem | SessionNotificationDetail;

/** Session fields the legacy survey subjects are built from. */
type SessionSubjectContext = { trainingPartner?: string | null; trainingTitle?: string | null };

/** Typeform-era surveys: orders no longer writes them, but the sent history still holds them. */
const LEGACY_SURVEY_EVENTS = new Set<string>([
  BookingEventsEnum.DIRECT_PEDAGOGICAL_REPORT,
  BookingEventsEnum.DELAYED_PEDAGOGICAL_REPORT,
]);

/** A rule step not fired yet: it has no order event, only its run step. */
export function isPendingRuleStep(entry: SessionNotificationEntry): boolean {
  return entry.eventIds.length === 0 && !!entry.workflowRunStepId;
}

export function isLegacySurveyNotification(entry: SessionNotificationEntry): boolean {
  return LEGACY_SURVEY_EVENTS.has(entry.eventName);
}

export function isTrainerNotification(entry: SessionNotificationEntry): boolean {
  return entry.recipientKind === SessionNotificationItemRecipientKindEnum.Trainer;
}

/** The fixed tag of the attachments cell: the event's own, or the invitation a trainer send carried. */
export function notificationEventTag(entry: SessionNotificationEntry): ISessionNotificationEventTag | undefined {
  const eventTag = SESSION_NOTIFICATION_EVENT_TAG.get(entry.eventName);
  if (eventTag) return eventTag;
  return isTrainerNotification(entry) && entry.hasCalendarInvitation ? CALENDAR_INVITATION_TAG : undefined;
}

/** Why orders sent a trainer notification by itself; null for one an admin sent. */
export function trainerNotificationCauseLabel(entry: SessionNotificationEntry): string | null {
  if (!isTrainerNotification(entry) || !entry.cause) return null;
  return TRAINER_NOTIFICATION_CAUSE_LABEL.get(entry.cause) ?? null;
}

/** An .ics that took a step out of the attendee's calendar. */
export function isCalendarWithdrawal(detail: SessionNotificationDetail): boolean {
  return (
    detail.eventName === BookingEventsEnum.SEND_ICS &&
    detail.calendarAction === SessionNotificationDetailCalendarActionEnum.Delete
  );
}

/** Sent and legacy rows are read by their order events, a pending rule step by its run step. */
export function notificationDetailRequest(entry: SessionNotificationEntry): GetSessionNotificationDetailDto {
  return entry.eventIds.length ? { eventIds: entry.eventIds } : { workflowRunStepId: entry.workflowRunStepId ?? null };
}

/** The first addresses inline, the rest folded into the « +N » pill (AC-ENVOI.4). */
export function notificationRecipients(
  entry: SessionNotificationEntry,
  visibleCount = SESSION_NOTIFICATION_VISIBLE_RECIPIENTS,
): { label: string; hidden: string[] } {
  const recipients = entry.recipients ?? [];

  if (!recipients.length) {
    if (isPendingRuleStep(entry)) {
      // Addresses are resolved when the step fires: a partner step without contact will be skipped.
      const label =
        entry.recipientKind === SessionNotificationItemRecipientKindEnum.Partner
          ? "Aucun contact prestataire"
          : "Tous les participants";
      return { label, hidden: [] };
    }
    return { label: "—", hidden: [] };
  }

  return {
    label: recipients.slice(0, visibleCount).join(", "),
    hidden: recipients.slice(visibleCount),
  };
}

/** Attachment names, the attendee-list snapshot first under its own label (AC-ENVOI.26). */
export function notificationAttachmentNames(entry: SessionNotificationEntry): { visible: string[]; hidden: string[] } {
  const documentNames = entry.attachments
    .filter((attachment) => !attachment.isAttendeeList)
    .map((attachment) => attachment.fileName);
  const names = [...new Set(documentNames)];
  if (entry.hasAttendeeList) names.unshift(ATTENDEE_LIST_ATTACHMENT_LABEL);

  return { visible: names.slice(0, 1), hidden: names.slice(1) };
}

/** Whoever sent it by hand, « Automatique » for rule-driven and system sends (AC-REGLE.8). */
export function notificationSenderLabel(entry: SessionNotificationEntry): string {
  const email = entry.sentBy?.email;
  if (entry.isAutomatic || !email || email === SimbelEmail.API) return AUTOMATIC_SENDER_LABEL;
  return email;
}

/** Legacy surveys store no subject: rebuild the one their mail carried. */
export function notificationSubject(entry: SessionNotificationEntry, session: SessionSubjectContext): string {
  if (entry.eventName === BookingEventsEnum.DIRECT_PEDAGOGICAL_REPORT) {
    return `${session.trainingPartner} - Evaluez votre formation ${session.trainingTitle}`;
  }
  if (entry.eventName === BookingEventsEnum.DELAYED_PEDAGOGICAL_REPORT) {
    return `${session.trainingPartner} - Evaluation de la formation ${session.trainingTitle} en tant que manager`;
  }
  return entry.subject || "-";
}

/** One row per document, listing every address it was served to, deduplicated by file name. */
export function notificationAttachmentRows(detail: SessionNotificationDetail): ISessionNotificationAttachmentRow[] {
  const rows: ISessionNotificationAttachmentRow[] = [];

  for (const group of detail.attachmentsByRecipient) {
    for (const attachment of group.attachments) {
      let row = rows.find((existing) => existing.attachment.fileName === attachment.fileName);
      if (!row) {
        row = { attachment, recipients: [] };
        rows.push(row);
      }
      if (!row.recipients.includes(group.email)) row.recipients.push(group.email);
    }
  }

  return rows;
}
